<script setup lang="ts">
  import { computed, nextTick, ref, watch } from "vue";
  import { Filter, Search } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Input } from "@/components/ui/input";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import {
    isCheckboxFilter,
    isDateRangeFilter,
    isNumberRangeFilter,
    type FilterConfig,
    type FilterOption,
    type FilterValues,
    type DateRangeValue,
    type NumberRangeValue,
    type CheckboxFilterConfig,
    type DateRangeFilterConfig,
    type NumberRangeFilterConfig,
  } from "./types";

  type OptionValue = string | boolean | number;

  const props = withDefaults(
    defineProps<{
      filters: FilterConfig[];
      activeFiltersCount?: number;
      matchingCount?: number;
      matchingLabel?: string;
      compact?: boolean;
    }>(),
    {
      activeFiltersCount: undefined,
      matchingCount: undefined,
      matchingLabel: "results",
      compact: true,
    },
  );

  const modelValue = defineModel<FilterValues>();

  const emit = defineEmits<{
    toggle: [filterId: string, value: OptionValue];
    "update:dateRange": [filterId: string, value: DateRangeValue];
    "update:numberRange": [filterId: string, value: NumberRangeValue];
    clear: [];
    apply: [];
  }>();

  const isModelMode = computed(() => modelValue.value !== undefined);
  const visibleFilters = computed(() => props.filters.filter((config) => config.isVisible !== false));

  const getCheckboxValues = (config: CheckboxFilterConfig): OptionValue[] => {
    if (isModelMode.value) {
      const val = modelValue.value?.[config.id];
      return Array.isArray(val) ? val : [];
    }
    return config.selectedValues || [];
  };
  const getDateRangeValue = (config: DateRangeFilterConfig): DateRangeValue | undefined => {
    if (isModelMode.value) {
      const val = modelValue.value?.[config.id];
      return val && typeof val === "object" && "start" in val ? (val as DateRangeValue) : undefined;
    }
    return config.value;
  };
  const getNumberRangeValue = (config: NumberRangeFilterConfig): NumberRangeValue | undefined => {
    if (isModelMode.value) {
      const val = modelValue.value?.[config.id];
      return val && typeof val === "object" && "min" in val ? (val as NumberRangeValue) : undefined;
    }
    return config.value;
  };
  const isRangeDefault = (config: NumberRangeFilterConfig, val?: NumberRangeValue) =>
    val === undefined || (val.min === (config.defaultMin ?? 0) && val.max === (config.defaultMax ?? 100));

  const filterCount = (config: FilterConfig): number => {
    if (isCheckboxFilter(config)) return getCheckboxValues(config).length;
    if (isDateRangeFilter(config)) {
      const val = getDateRangeValue(config);
      return val?.start || val?.end ? 1 : 0;
    }
    if (isNumberRangeFilter(config)) return isRangeDefault(config, getNumberRangeValue(config)) ? 0 : 1;
    return 0;
  };

  const computedActiveFiltersCount = computed(
    () => props.activeFiltersCount ?? visibleFilters.value.reduce((sum, config) => sum + filterCount(config), 0),
  );
  const hasActiveFilters = computed(() => computedActiveFiltersCount.value > 0);

  const open = ref(false);
  const selectedId = ref<string | null>(null);
  const valueQuery = ref("");
  const searchInput = ref<InstanceType<typeof Input> | null>(null);
  const criteriaRail = ref<HTMLElement | null>(null);

  const selected = computed<FilterConfig | null>(
    () => visibleFilters.value.find((config) => config.id === selectedId.value) ?? visibleFilters.value[0] ?? null,
  );

  const orderSnapshot = ref<OptionValue[]>([]);
  const takeOrderSnapshot = () => {
    const config = selected.value;
    if (!config || !isCheckboxFilter(config)) return (orderSnapshot.value = []);
    const picked = getCheckboxValues(config);
    const rest = config.options.filter((option) => !picked.includes(option.value));
    if (!config.keepOptionsOrder) {
      rest.sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || String(a.label).localeCompare(String(b.label)));
    }
    orderSnapshot.value = [...picked, ...rest.map((option) => option.value)];
  };

  const focusSearch = () => nextTick(() => searchInput.value?.focus());

  const selectCriterion = (id: string) => {
    selectedId.value = id;
    valueQuery.value = "";
    takeOrderSnapshot();
    focusSearch();
  };

  watch(open, (isOpen) => {
    if (!isOpen) return;
    if (!selected.value || !visibleFilters.value.some((config) => config.id === selectedId.value)) {
      selectedId.value = visibleFilters.value[0]?.id ?? null;
    }
    valueQuery.value = "";
    takeOrderSnapshot();
    focusSearch();
    nextTick(() => criteriaRail.value?.querySelector<HTMLElement>('[aria-pressed="true"]')?.scrollIntoView({ block: "nearest" }));
  });

  const orderedOptions = computed<FilterOption[]>(() => {
    const config = selected.value;
    if (!config || !isCheckboxFilter(config)) return [];
    const byValue = new Map(config.options.map((option) => [option.value, option]));
    const known = orderSnapshot.value.map((value) => byValue.get(value)).filter((option): option is FilterOption => !!option);
    const fresh = config.options.filter((option) => !orderSnapshot.value.includes(option.value));
    return [...known, ...fresh];
  });

  const visibleOptions = computed(() => {
    const query = valueQuery.value.trim().toLowerCase();
    if (!query) return orderedOptions.value;
    return orderedOptions.value.filter((option) => String(option.label).toLowerCase().includes(query));
  });

  const isChecked = (config: CheckboxFilterConfig, value: OptionValue) => getCheckboxValues(config).includes(value);

  const writeModel = (filterId: string, value: FilterValues[string] | undefined) => {
    const next = { ...modelValue.value };
    if (value === undefined) delete next[filterId];
    else next[filterId] = value;
    modelValue.value = next;
  };

  const toggleValue = (config: CheckboxFilterConfig, value: OptionValue) => {
    if (!isModelMode.value) return emit("toggle", config.id, value);
    const current = [...getCheckboxValues(config)];
    const index = current.indexOf(value);
    if (index === -1) current.push(value);
    else current.splice(index, 1);
    writeModel(config.id, current.length ? current : undefined);
  };

  const updateDateRange = (config: DateRangeFilterConfig, patch: Partial<DateRangeValue>) => {
    const value = { start: getDateRangeValue(config)?.start || "", end: getDateRangeValue(config)?.end || "", ...patch };
    if (!isModelMode.value) return emit("update:dateRange", config.id, value);
    writeModel(config.id, value.start || value.end ? value : undefined);
  };

  const updateNumberRange = (config: NumberRangeFilterConfig, patch: Partial<NumberRangeValue>) => {
    const current = getNumberRangeValue(config);
    const value = { min: current?.min ?? config.defaultMin ?? 0, max: current?.max ?? config.defaultMax ?? 100, ...patch };
    if (!isModelMode.value) return emit("update:numberRange", config.id, value);
    writeModel(config.id, isRangeDefault(config, value) ? undefined : value);
  };

  const handleClear = () => {
    if (isModelMode.value) modelValue.value = {};
    emit("clear");
  };

  const handleApply = () => {
    emit("apply");
    open.value = false;
  };

  const matchingText = computed(() => {
    if (props.matchingCount === undefined) return null;
    if (props.matchingCount === 1) return `1 ${props.matchingLabel.replace(/s$/, "")} matches`;
    return `${props.matchingCount.toLocaleString()} ${props.matchingLabel} match`;
  });
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        v-if="compact"
        variant="ghost"
        size="icon"
        class="relative text-muted-foreground hover:text-primary"
        aria-label="Filters"
        title="Filters"
        data-cy="filters-trigger">
        <Filter
          :stroke-width="1.8"
          class="size-4" />
        <span
          v-if="computedActiveFiltersCount > 0"
          class="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-ring" />
      </Button>
      <Button
        v-else
        variant="outline"
        class="gap-2 bg-background font-semibold text-primary"
        data-cy="filters-trigger">
        <Filter
          :stroke-width="1.8"
          class="size-3.5" />
        Filters
        <span
          v-if="computedActiveFiltersCount > 0"
          class="rounded-full bg-primary px-1.5 py-px text-2xs font-bold leading-4 text-primary-foreground tabular-nums">
          {{ computedActiveFiltersCount }}
        </span>
      </Button>
    </PopoverTrigger>

    <PopoverContent
      align="start"
      :side-offset="8"
      :collision-padding="16"
      class="w-130 overflow-hidden rounded-xl p-0 shadow-lg">
      <div class="grid h-65 grid-cols-[180px_minmax(0,1fr)]">
        <nav
          ref="criteriaRail"
          class="flex flex-col gap-0.5 overflow-y-auto border-r bg-page p-2"
          aria-label="Filter criteria">
          <button
            v-for="config in visibleFilters"
            :key="config.id"
            type="button"
            class="flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left text-13 transition-colors"
            :class="
              selected?.id === config.id
                ? 'bg-background font-semibold text-primary shadow-sm'
                : 'text-foreground-secondary hover:bg-secondary'
            "
            :aria-pressed="selected?.id === config.id"
            @click="selectCriterion(config.id)">
            <span class="min-w-0 flex-1 truncate">{{ config.label }}</span>
            <span
              v-if="filterCount(config)"
              class="rounded-full bg-secondary px-1.5 text-2xs font-semibold leading-4 text-foreground-secondary tabular-nums">
              {{ filterCount(config) }}
            </span>
          </button>
        </nav>

        <section
          v-if="selected"
          class="flex min-h-0 min-w-0 flex-col">
          <template v-if="isCheckboxFilter(selected)">
            <div
              v-if="selected.hasSearch !== false"
              class="p-2.5 pb-1.5">
              <Input
                ref="searchInput"
                v-model="valueQuery"
                :icon="Search"
                placeholder="Search…"
                class="h-8 text-13" />
            </div>
            <div
              v-if="visibleOptions.length"
              class="min-h-0 flex-1 overflow-y-auto px-2.5 py-2">
              <label
                v-for="option in visibleOptions"
                :key="String(option.value)"
                class="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-2 text-13 text-primary transition-colors hover:bg-secondary">
                <Checkbox
                  class="size-4.5 rounded-md border-input data-[state=checked]:border-ring data-[state=checked]:bg-ring"
                  :model-value="isChecked(selected, option.value)"
                  @update:model-value="toggleValue(selected as CheckboxFilterConfig, option.value)" />
                <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
                <span
                  v-if="option.count !== undefined"
                  class="text-xs tabular-nums text-foreground-subtle">
                  {{ option.count }}
                </span>
              </label>
            </div>
            <div
              v-else
              class="flex flex-1 flex-col items-center justify-center gap-1.5 px-4 text-center">
              <p class="text-13 text-foreground-secondary">No {{ selected.label.toLowerCase() }} matches</p>
              <p class="text-xs text-foreground-subtle">Check the spelling or clear your search</p>
            </div>
          </template>

          <div
            v-else-if="isDateRangeFilter(selected)"
            class="space-y-3 p-3">
            <label class="block space-y-1">
              <span class="text-xs text-muted-foreground">{{ selected.startLabel ?? "Start date" }}</span>
              <Input
                :model-value="getDateRangeValue(selected)?.start || ''"
                type="date"
                class="h-8 text-13"
                @update:model-value="updateDateRange(selected as DateRangeFilterConfig, { start: String($event) })" />
            </label>
            <label class="block space-y-1">
              <span class="text-xs text-muted-foreground">{{ selected.endLabel ?? "End date" }}</span>
              <Input
                :model-value="getDateRangeValue(selected)?.end || ''"
                type="date"
                class="h-8 text-13"
                @update:model-value="updateDateRange(selected as DateRangeFilterConfig, { end: String($event) })" />
            </label>
          </div>

          <div
            v-else-if="isNumberRangeFilter(selected)"
            class="flex items-end gap-2 p-3">
            <label class="block space-y-1">
              <span class="text-xs text-muted-foreground">{{ selected.minLabel ?? "Min" }}</span>
              <Input
                :model-value="getNumberRangeValue(selected)?.min ?? selected.defaultMin ?? 0"
                type="number"
                :min="selected.minLimit"
                :max="selected.maxLimit"
                class="h-8 w-24 text-13"
                @update:model-value="updateNumberRange(selected as NumberRangeFilterConfig, { min: Number($event) })" />
            </label>
            <span class="pb-2 text-muted-foreground">–</span>
            <label class="block space-y-1">
              <span class="text-xs text-muted-foreground">{{ selected.maxLabel ?? "Max" }}</span>
              <Input
                :model-value="getNumberRangeValue(selected)?.max ?? selected.defaultMax ?? 100"
                type="number"
                :min="selected.minLimit"
                :max="selected.maxLimit"
                class="h-8 w-24 text-13"
                @update:model-value="updateNumberRange(selected as NumberRangeFilterConfig, { max: Number($event) })" />
            </label>
          </div>
        </section>
      </div>

      <footer class="flex h-16 items-center gap-4 border-t bg-background px-4">
        <div class="min-w-0 flex-1">
          <slot name="footer-start" />
        </div>
        <span class="shrink-0 text-13 text-muted-foreground">{{ matchingText }}</span>
        <div class="flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            class="text-foreground-secondary"
            :disabled="!hasActiveFilters"
            @click="handleClear">
            Reset
          </Button>
          <Button
            size="sm"
            @click="handleApply">
            Apply
          </Button>
        </div>
      </footer>
    </PopoverContent>
  </Popover>
</template>
